import { ENEMIES } from '../../data/enemies';
import { SIM_HZ } from '../../data/tuning';
import { createCircle, type Circle } from '../collision';

/**
 * EN-32 Sombrero volador (GDD §8.3, issue #117). Don Nieto is a thrower on a balcony (`IDLE`, `WINDUP`,
 * `THROWN`, `COOLDOWN`) that throws a sombrero on a fixed cadence while the balcony is ahead of the plane;
 * the sombrero is a boomerang: it goes out to the left (aimed at the plane's height at the throw) and comes
 * back to the balcony, harmful on both legs. Pure and deterministic: the path is computed from the ticks
 * since the throw, never accumulated. With the bribe active Don Nieto looks away and does not throw.
 */

export const EN32 = ENEMIES['EN-32'];

export const WINDUP_TICKS = Math.round(EN32.windupSeconds * SIM_HZ);
export const CADENCE_TICKS = Math.round(EN32.cadenceSeconds * SIM_HZ);
export const FLIGHT_TICKS = Math.round(EN32.flightSeconds * SIM_HZ);
/** Ticks of the outbound and the return legs (half each). */
export const LEG_TICKS = FLIGHT_TICKS / 2;

export type ThrowerPhase = 'IDLE' | 'WINDUP' | 'THROWN' | 'COOLDOWN';

export interface Sombrero {
  /** Ticks since it was thrown. */
  age: number;
  /** Height the throw was aimed at (the plane's height when it left the balcony). */
  aimY: number;
}

export interface SombreroThrowerState {
  phase: ThrowerPhase;
  /** Ticks spent in the current phase. */
  phaseTicks: number;
  /** Ticks since the last throw (for the cadence). */
  sinceThrow: number;
  /** The sombrero in flight, if any. */
  sombrero: Sombrero | null;
  /** Total sombreros thrown, for tests. */
  thrown: number;
}

export const createSombreroThrower = (): SombreroThrowerState => ({
  phase: 'IDLE',
  phaseTicks: 0,
  sinceThrow: CADENCE_TICKS,
  sombrero: null,
  thrown: 0,
});

/**
 * One tick of the thrower. `balcony` is its world position, `planeY` the height to aim at, `ahead` whether
 * the balcony is on screen and to the right of the plane (only then does it throw) and `bribe` whether the
 * Soborno is active (it never throws, §7). Returns the events of the tick: `windup` (the warning), `thrown`
 * and `caught`.
 */
export interface ThrowerStep {
  windup: boolean;
  thrown: boolean;
  caught: boolean;
}

export function stepSombreroThrower(
  state: SombreroThrowerState,
  _balcony: { x: number; y: number },
  planeY: number,
  ahead: boolean,
  bribe: boolean,
): ThrowerStep {
  const events: ThrowerStep = { windup: false, thrown: false, caught: false };
  state.phaseTicks += 1;
  state.sinceThrow += 1;

  // The sombrero in flight ages wherever it is; it is caught when the return leg ends.
  if (state.sombrero) {
    state.sombrero.age += 1;
    if (state.sombrero.age >= FLIGHT_TICKS) {
      state.sombrero = null;
      events.caught = true;
    }
  }

  switch (state.phase) {
    case 'IDLE':
      // Start a wind-up when it can throw: ahead of the plane, no bribe, no sombrero in flight, cadence met.
      if (ahead && !bribe && !state.sombrero && state.sinceThrow >= CADENCE_TICKS) {
        state.phase = 'WINDUP';
        state.phaseTicks = 0;
        events.windup = true;
      }
      break;
    case 'WINDUP':
      // Keep winding up only while it can still throw; otherwise it stands down.
      if (!ahead || bribe) {
        state.phase = 'IDLE';
        state.phaseTicks = 0;
        break;
      }
      if (state.phaseTicks >= WINDUP_TICKS) {
        state.sombrero = { age: 0, aimY: planeY };
        state.thrown += 1;
        state.sinceThrow = 0;
        state.phase = 'THROWN';
        state.phaseTicks = 0;
        events.thrown = true;
      }
      break;
    case 'THROWN':
      // One throw per cadence: wait for the sombrero to come back before considering another.
      if (!state.sombrero) {
        state.phase = 'COOLDOWN';
        state.phaseTicks = 0;
      }
      break;
    case 'COOLDOWN':
      if (state.sinceThrow >= CADENCE_TICKS) {
        state.phase = 'IDLE';
        state.phaseTicks = 0;
      }
      break;
  }

  return events;
}

/**
 * Position of the sombrero in flight, from the ticks since the throw. Outbound leg: from the balcony to
 * `reach` u to the left, aimed at `aimY`. Return leg: back to the balcony, `returnDrop` u below the
 * outbound path, so out and back are not the same line. Returns null when there is no sombrero.
 */
export function sombreroPosition(state: SombreroThrowerState, balcony: { x: number; y: number }): { x: number; y: number } | null {
  const sombrero = state.sombrero;
  if (!sombrero) return null;
  const t = sombrero.age / FLIGHT_TICKS;
  // A symmetric horizontal sweep: 0 at the balcony, 1 at the far point (halfway), 0 again at the balcony.
  const out = 1 - Math.abs(2 * t - 1);
  const x = balcony.x - EN32.reach * out;
  // Outbound and return trace the same line from the balcony to aimY; the return drops `returnDrop` below it
  // everywhere (and is exactly at the balcony when caught), so both legs are not the same path (#117).
  const lineY = balcony.y + (sombrero.aimY - balcony.y) * out;
  const y = lineY - (t > 0.5 ? EN32.returnDrop * out : 0);
  return { x, y };
}

/** The sombrero circle, 70-80 % of the model (GDD §8.1). */
export const sombreroCircle = (at: { x: number; y: number }): Circle => createCircle(at.x, at.y, EN32.hitboxRadius);

/** Whether the sombrero is on its outbound leg (before the far point). */
export const isOutbound = (state: SombreroThrowerState): boolean => (state.sombrero?.age ?? 0) < LEG_TICKS;
